import {useCallback} from 'react';

import {updateMyTeamsFilter} from '/redux/action/myTeams.action';

import {getBonusFlags} from '../filters/helpers/getBonusFlags.util';
import {FilterOptionProps} from '../MyTeamsContent.types';
import {generateOnApplyMyTeamsFilters} from '../MyTeamsReport.util';
import {buildFilterAnalyticsPayload} from '../util/buildFilterAnalyticsPayload.util';
import {getReportName} from '../util/myTeamsAnalytics.util';
import {
  hasFiltersApplied,
  isDefaultFilters,
} from '../util/myTeamsFilterDomain.util';

import {useClearMyTeamsFilters} from './useClearMyTeamsFilters';
import {useMyTeamsContext} from './useMyTeamsContext';

type Props = {
  frontlineOnly: boolean;
  bonusOptions?: FilterOptionProps[];
  defaultNewCoachesKey: string;
  defaultNewCoachesValue: string;
  closeToNextRank?: boolean;

  ffTeamsOrgSR: boolean;

  effectiveLastPaidRank: string;
  effectiveHighestAchievedRank: string;
  effectiveCoachCurrentRank: string;
  effectiveUserId: string;

  updateCurrentTabFilters: (filters: Partial<any>) => void;
};

export const useFrontlineFilter = ({
  frontlineOnly,
  bonusOptions,
  defaultNewCoachesKey,
  defaultNewCoachesValue,
  closeToNextRank,
  ffTeamsOrgSR,
  effectiveLastPaidRank,
  effectiveHighestAchievedRank,
  effectiveCoachCurrentRank,
  effectiveUserId,
  updateCurrentTabFilters,
}: Props) => {
  const {dispatch, filtersRedux, reduxActiveTab, reduxIsViewTeam} =
    useMyTeamsContext();
  const clearMyTeamsFilters = useClearMyTeamsFilters();

  const toggleFrontlineFilter = useCallback(() => {
    const newFrontlineOnly = !frontlineOnly;

    const {cabEarned, aabEarned} = getBonusFlags(bonusOptions);

    const updatedFilters = {
      ...filtersRedux,
      frontlineOnly: newFrontlineOnly,
      cabEarned,
      aabEarned,
      closeToNextRank,
    };

    if (
      isDefaultFilters(
        updatedFilters,
        defaultNewCoachesKey,
        defaultNewCoachesValue,
      )
    ) {
      updateCurrentTabFilters({frontlineOnly: false});

      clearMyTeamsFilters(defaultNewCoachesKey, newFrontlineOnly);
      return;
    }

    updateCurrentTabFilters({
      frontlineOnly: newFrontlineOnly,
    });
    dispatch(updateMyTeamsFilter(updatedFilters));

    //Build analytics payload
    const filterPayload = buildFilterAnalyticsPayload({
      updatedFilters,
      newFrontlineOnly,
      defaultNewCoachesKey,
      ffTeamsOrgSR,
    });

    if (hasFiltersApplied(updatedFilters, defaultNewCoachesKey)) {
      generateOnApplyMyTeamsFilters({
        ...filterPayload,
        lastPaidRank: effectiveLastPaidRank,
        coachHighestRank: effectiveHighestAchievedRank,
        coachCurrentRank: effectiveCoachCurrentRank,
        coachId: String(effectiveUserId),
        reportName: getReportName(reduxIsViewTeam, reduxActiveTab),
      });
    }
  }, [
    frontlineOnly,
    bonusOptions,
    filtersRedux,
    closeToNextRank,
    defaultNewCoachesKey,
    defaultNewCoachesValue,
    updateCurrentTabFilters,
    dispatch,
    ffTeamsOrgSR,
    clearMyTeamsFilters,
    effectiveLastPaidRank,
    effectiveHighestAchievedRank,
    effectiveCoachCurrentRank,
    effectiveUserId,
    reduxIsViewTeam,
    reduxActiveTab,
  ]);

  return {toggleFrontlineFilter};
};
